<template>
  <Teleport to="body">
    <Transition name="question-modal">
      <div
        v-if="isOpen"
        class="question-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="question-modal-title"
        @click.self="close"
      >
        <div class="question-modal__backdrop" aria-hidden="true" />
        <div class="question-modal__box">
          <button type="button" class="question-modal__close" aria-label="Закрыть" @click="close">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
          <h2 id="question-modal-title" class="question-modal__title">Напишите нам</h2>
          <form class="question-modal__form" @submit.prevent="submit">
            <FormInput
              v-model="form.name"
              name="name"
              label="Ваше имя"
              placeholder="Как к вам обращаться?"
              :required="true"
            />
            <FormInput
              v-model="form.questionText"
              name="questionText"
              label="Текст вашего вопроса"
              placeholder="Опишите ваш вопрос"
              :required="true"
              multiline
              :rows="5"
            />
            <FormInput
              v-model="form.contacts"
              name="contacts"
              label="Ваши контакты"
              placeholder="Телефон, почта или мессенджер"
            />
            <div class="question-modal__actions">
              <button type="button" class="question-modal__btn question-modal__btn_secondary" @click="close">Отмена</button>
              <button type="submit" class="question-modal__btn question-modal__btn_submit" :disabled="sending">
                {{ sending ? 'Отправка…' : 'Отправить' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
import { ref, reactive, computed, watch } from 'vue'
import { useStore } from 'vuex'
import { getApiUrl } from '@/config/api'
import FormInput from '@/components/FormInput.vue'

const defaultForm = () => ({ name: '', questionText: '', contacts: '' })

export default {
  name: 'QuestionModal',
  components: { FormInput },
  setup() {
    const store = useStore()
    const isOpen = computed(() => store.state.questionModalOpen)
    const sending = ref(false)
    const form = reactive(defaultForm())

    function close() {
      store.commit('setQuestionModalOpen', false)
    }

    async function submit() {
      if (!form.name.trim() || !form.questionText.trim()) return
      sending.value = true
      try {
        const res = await fetch(getApiUrl('/api/questions'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: form.name.trim(),
            questionText: form.questionText.trim(),
            contacts: form.contacts.trim()
          })
        })
        const data = await res.json().catch(() => ({}))
        if (!res.ok) {
          throw new Error(data.error || `HTTP ${res.status}`)
        }
        close()
        store.commit('setToast', 'Спасибо за ваш вопрос!')
        Object.assign(form, defaultForm())
      } catch (e) {
        console.error('Question submit error', e)
        store.commit('setToast', e.message || 'Ошибка отправки. Попробуйте позже.')
      } finally {
        sending.value = false
      }
    }

    watch(isOpen, (open) => {
      if (open) {
        Object.assign(form, defaultForm())
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth
        document.body.style.paddingRight = scrollbarWidth ? `${scrollbarWidth}px` : ''
        document.body.style.overflow = 'hidden'
      } else {
        document.body.style.overflow = ''
        document.body.style.paddingRight = ''
      }
    })

    return { isOpen, form, sending, close, submit }
  }
}
</script>

<style lang="scss" scoped>
@import "../styles/_variables.scss";

$dur: 0.3s;
$ease: cubic-bezier(0.22, 1, 0.36, 1);

.question-modal {
  position: fixed;
  inset: 0;
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  font-family: 'Arimo', 'Helvetica Neue', Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
}

.question-modal__backdrop {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
}

.question-modal__box {
  position: relative;
  width: 100%;
  max-width: 480px;
  max-height: 90vh;
  overflow-y: auto;
  background: $color-primary-inverse;
  border-radius: 16px;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.25);
  padding: 32px 28px 28px;
  box-sizing: border-box;
}

.question-modal__close {
  position: absolute;
  top: 20px;
  right: 20px;
  width: 40px;
  height: 40px;
  padding: 0;
  border: none;
  background: rgba(0, 0, 0, 0.06);
  border-radius: 50%;
  color: $color-text-muted;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s, color 0.2s;

  &:hover {
    background: rgba(0, 0, 0, 0.1);
    color: $color-text;
  }
  svg { width: 20px; height: 20px; }
}

.question-modal__title {
  font-size: 22px;
  font-weight: 800;
  letter-spacing: -0.02em;
  margin: 0 0 24px;
  padding-right: 48px;
  color: $color-text;
}

.question-modal__form {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.question-modal__actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 8px;
  padding-top: 20px;
  border-top: 1px solid $color-border;
}

.question-modal__btn {
  padding: 12px 24px;
  font-size: 15px;
  font-weight: 600;
  border-radius: 10px;
  border: none;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.2s, opacity 0.2s;

  &_secondary {
    background: rgba(0, 0, 0, 0.06);
    color: $color-text;
    &:hover { background: rgba(0, 0, 0, 0.1); }
  }

  &_submit {
    background: $color-accent-teal;
    color: $color-primary-inverse;
    &:hover:not(:disabled) { background: darken($color-accent-teal, 6%); }
    &:disabled { opacity: 0.7; cursor: not-allowed; }
  }
}

.question-modal-enter-active,
.question-modal-leave-active {
  transition: opacity $dur $ease;
  .question-modal__box {
    transition: transform $dur $ease;
  }
}
.question-modal-enter-from,
.question-modal-leave-to {
  opacity: 0;
  .question-modal__box {
    transform: scale(0.96) translateY(12px);
  }
}
</style>
